import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { addIcons } from 'ionicons';
import { checkmarkOutline, copyOutline } from 'ionicons/icons';
import { QrCodeRecord } from './app.types';

@Component({
  selector: 'app-qr-preview',
  standalone: false,
  template: `
    <section class="preview" *ngIf="visible">
      <ion-grid class="preview-grid">
        <ion-row class="ion-justify-content-center">
          <ion-col size="12" size-sm="10" size-md="8" size-lg="10" size-xl="8">
            <ion-img
              *ngIf="generatedImageUrl"
              class="qr-preview-image"
              [src]="generatedImageUrl"
              alt="Generated QR code preview"
            ></ion-img>
            <canvas #qrCanvas class="ion-hide" aria-hidden="true"></canvas>

            <div class="details" *ngIf="generatedRecord">
              <strong>{{ generatedRecord.title || generatedRecord.redirect_path }}</strong>
              <span class="qr-url-row">
                <span>{{ generatedRecord.target_url }}</span>
                <ion-button
                  type="button"
                  class="qr-copy-button"
                  size="small"
                  fill="clear"
                  [title]="copyTitle(generatedRecord.target_url)"
                  [attr.aria-label]="copyTitle(generatedRecord.target_url)"
                  (click)="copyUrl(generatedRecord.target_url)"
                >
                  <ion-icon
                    slot="icon-only"
                    [name]="copiedUrl === generatedRecord.target_url ? 'checkmark-outline' : 'copy-outline'"
                    aria-hidden="true"
                  ></ion-icon>
                </ion-button>
              </span>
              <code>{{ generatedRecord.redirect_path }}</code>
              <span>{{ generatedRecord.scan_count }} scans</span>
            </div>

            <div class="details" *ngIf="directQrUrl && !generatedRecord">
              <strong>Direct QR</strong>
              <span class="qr-url-row">
                <span>{{ directQrUrl }}</span>
                <ion-button
                  type="button"
                  class="qr-copy-button"
                  size="small"
                  fill="clear"
                  [title]="copyTitle(directQrUrl)"
                  [attr.aria-label]="copyTitle(directQrUrl)"
                  (click)="copyUrl(directQrUrl)"
                >
                  <ion-icon
                    slot="icon-only"
                    [name]="copiedUrl === directQrUrl ? 'checkmark-outline' : 'copy-outline'"
                    aria-hidden="true"
                  ></ion-icon>
                </ion-button>
              </span>
              <code>No API redirect or analytics</code>
            </div>
          </ion-col>
        </ion-row>
      </ion-grid>
    </section>
  `
})
export class QrPreviewComponent {
  @Input() visible = false;
  @Input() generatedRecord: QrCodeRecord | null = null;
  @Input() directQrUrl = '';
  @Input() generatedImageUrl = '';

  @ViewChild('qrCanvas') private readonly qrCanvas?: ElementRef<HTMLCanvasElement>;

  copiedUrl = '';
  private copyResetTimeout: number | undefined;

  constructor() {
    addIcons({ checkmarkOutline, copyOutline });
  }

  get canvas(): HTMLCanvasElement | undefined {
    return this.qrCanvas?.nativeElement;
  }

  copyTitle(url: string): string {
    return this.copiedUrl === url ? 'Copied' : 'Copy URL';
  }

  async copyUrl(url: string): Promise<void> {
    if (!url) {
      return;
    }

    await this.writeClipboard(url);
    this.copiedUrl = url;
    window.clearTimeout(this.copyResetTimeout);
    this.copyResetTimeout = window.setTimeout(() => {
      this.copiedUrl = '';
    }, 1800);
  }

  private async writeClipboard(value: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
